import React, { useEffect, useState } from 'react';
import { IActivityType, IAsset } from "../models/IAsset";
import {Card, Row, Col, Image, ListGroup, Button, Modal} from 'react-bootstrap';
import {
    X,
    Trash2,
    Stethoscope,
    Barcode,
    MapPin,
    ClipboardX,
    ClipboardPen,
    List,
    ThumbsUp,
    Wrench,
    File,
    Sparkles,
    Play,
    ArrowRightLeft,
    Camera, StepBack, StepForward, ArrowBigLeft, ArrowBigRight
} from 'lucide-react';
import assetImg from '../assets/images/dispenser.png'
import { getActivityTypes, requestActivity } from "../actions/activityActions";
import { connect } from "react-redux";
import UIButton from "./utilities/UIButton";
import { Utility } from "../utilities/utility";
import * as Yup from 'yup';
import BottomSheet from "./bottom-sheet";
import { Form as IKForm, Formik, FormikHelpers } from "formik";
import FileUploadField from "./utilities/FileUpload";
import { Form } from 'react-bootstrap';
import ScannerField from "./utilities/ScannerField";
import { SingleValue } from "react-select";
import { Option } from "../models/types";
import { useTranslation } from "react-i18next";
import FancyButton from "./utilities/FancyButton";
import '../assets/styles/App.css';
import {CameraRBModal} from "./utilities/NativeCamera";
import {upperFirst} from "lodash";

interface Props {
    asset: IAsset;
    activityTypes: IActivityType[];
    dispatch: Function;
    showControls?: boolean;
}

interface FormValues {
    file: File | null;
    location_id: SingleValue<Option>;
    activity_type_id: number | undefined;
    remark: string;
    contact_person: string;
    priority: number | undefined;
}

const Asset: React.FC<Props> = ({ asset, activityTypes, dispatch, showControls = true }) => {
    const [isSheetOpen, setIsSheetOpen] = useState(false);
    const [action, setAction] = useState('');
    const [show, setShow] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingReset, setPendingReset] = useState<null | (() => void)>(null);
    const [cancelQR, setCancelQR] = useState(false);

    const [iconShakeInterval, setIconShakeInterval] = useState<number|undefined>(undefined);
    const { i18n } = useTranslation();
    // @ts-ignore
    const t = (txt: string, defaultObj?: object) => i18n.t(txt, defaultObj);

    const cleanActivityType = activityTypes.find((activityType) => activityType.name.toLocaleLowerCase() === 'clean')
    const repairActivityType = activityTypes.find((activityType) => activityType.name.toLocaleLowerCase() === 'repair')
    const moveActivityType = activityTypes.find((activityType) => activityType.name.toLocaleLowerCase() === 'move')

    const closeSheet = () => {
        setIsSheetOpen(false);
        setIconShakeInterval(undefined);
    };

    useEffect(function () {
        dispatch(getActivityTypes())
    }, [])

    const circleStyle = (tone = 'primary'): React.CSSProperties  => (
        {
            width: 30,
            height: 30,
            background: `var(--bs-${tone})`,
            borderRadius: '50%',
            color: 'var(--bs-white)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            flex: '0 0 auto',
        }
    );


    const initialValues: FormValues = { file: null, location_id: {value: '', label: ''},
        contact_person: '', remark: '', priority: undefined, activity_type_id: undefined };

    const handleSubmit = (values: FormValues, actions: FormikHelpers<FormValues>) => {
        handleRequestActivity(action, values)
        actions.setSubmitting(false);
    };

    const getValidationSchema = (action: string) =>
        Yup.object({
            file: Yup.mixed<File>()
                .nullable()
                .notRequired()
                .test('fileSize', 'File too large', (value) => !value || value.size <= 1024 * 1024)
                .test('fileFormat', 'Unsupported format', (value) =>
                    !value || ['image/jpeg', 'image/png'].includes(value.type)
                ),

            priority: Yup.number()
                .transform((val, orig) => (orig === '' ? undefined : Number(orig)))
                .typeError('Priority is required')
                .required('Priority is required'),

            ...(action === 'other'
                ? {
                    activity_type_id: Yup.number()
                        .transform((val, orig) => (orig === '' ? undefined : Number(orig)))
                        .typeError('Activity type is required')
                        .required('Activity type is required'),
                }
                : {}),
        });

    const handleRequestActivity = (action: string, data?: FormValues) => {
        switch (action) {
            case 'clean':
                dispatch(requestActivity({
                    'asset_id': asset.id,
                    'activity_type_id': cleanActivityType?.id,
                    'remark': data?.remark,
                    'contact_person': data?.contact_person,
                    'priority': data?.priority,
                    'location_id': data?.location_id?.value,
                    'file': data?.file,
                }, 'clean-btn'))
                break
            case 'repair':
                dispatch(requestActivity({
                    'asset_id': asset.id,
                    'activity_type_id': repairActivityType?.id,
                    'remark': data?.remark,
                    'contact_person': data?.contact_person,
                    'priority': data?.priority,
                    'file': data?.file,
                }, 'repair-btn'))
                break
            case 'pickup':
                dispatch(requestActivity({
                    'asset_id': asset.id,
                    'activity_type_id': moveActivityType?.id,
                    'remark': data?.remark,
                    'contact_person': data?.contact_person,
                    'priority': data?.priority,
                    'location_id': data?.location_id?.value,
                    'file': data?.file,
                }, 'pickup-btn'))
                break
            default :
                dispatch(requestActivity({
                    'asset_id': asset.id,
                    'activity_type_id': data?.activity_type_id,
                    'remark': data?.remark,
                    'contact_person': data?.contact_person,
                    'priority': data?.priority,
                    'location_id': data?.location_id?.value,
                    'file': data?.file,
                }, 'pickup-btn'))
        }
    }

    const handleAction = (action: string) => {
        setAction(action);
        setIsSheetOpen(true)
        setIconShakeInterval(4000);
    }
    let inspectionHasExpired = false;
    const coveredTo = Utility.formatAsDate(asset.last_inspection?.cover_to);
    if (coveredTo) {
        inspectionHasExpired = new Date() > coveredTo;
    }

    const removeImage = (setFieldValue: (n: string, v: any, s?: boolean) => void) => {
        setFieldValue('file', null, true);   // not "photo"
        if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
    };


    return (
        <div className="mt-4 border-0">
            <Card style={{ width: '100%', padding: '1px', border: 0, minHeight: '570px' }}>
                <Row className='gutterless-row' style={{ borderBottom: 'solid 1px #d2d2d2' }}>
                    <Card.Title className="text-center text-capitalize" style={{fontSize: 17}}>
                        {asset?.name || asset.asset_type?.name || asset.supplier?.name || asset.manufacturer?.name}
                    </Card.Title>
                </Row>
                <Row className='gutterless-row' style={{borderBottom: '1px solid rgb(210, 210, 210)'}}>
                    {/* Details Section */}
                    <Col xs={8}>
                        <Card.Body className="p-0">
                            {/* Details List */}
                            <ListGroup variant="flush" className="border-0">
                                <ListGroup.Item className="d-flex align-items-center text-capitalize border-0">
                                    <span className="p-1 me-2" style={circleStyle()}><Stethoscope size={16}/></span> {asset.identifier}
                                </ListGroup.Item>
                                <ListGroup.Item className="d-flex align-items-center text-capitalize border-0">
                                    <span className="p-1 me-2" style={circleStyle()}><Barcode size={16}/></span> {asset.serial_number ? asset.serial_number : (<span style={{ fontWeight: 'bold', fontSize: '10px', paddingTop: '.25rem' }} className="text-danger">{t('serial number unknown')}</span>)}
                                </ListGroup.Item>
                                <ListGroup.Item className="d-flex align-items-center text-capitalize border-0" style={{paddingRight: 40}}>
                                    <span className="p-1 me-2" style={circleStyle()}><List size={16}/></span> {asset.manufacturer?.name ? Utility.shorten(asset.manufacturer.name, 12) : (<span style={{ fontWeight: 'bold', fontSize: '10px', paddingTop: '.25rem' }} className="text-danger">{t('manufacturer unknown')}</span>)}
                                </ListGroup.Item>
                                <ListGroup.Item className="d-flex align-items-center text-capitalize border-0" style={{paddingRight: 40}}>
                                    <span className="p-1 me-2" style={circleStyle()}><List size={16}/></span> {asset.global_gtin?.model ? Utility.shorten(asset.global_gtin?.model, 12) : (<span style={{ fontWeight: 'bold', fontSize: '10px', paddingTop: '.25rem' }} className="text-danger">{t('model unknown')}</span>)}
                                </ListGroup.Item>
                                <ListGroup.Item className="d-flex align-items-center text-capitalize border-0" style={{paddingRight: 40}}>
                                    <span className="p-1 me-2" style={circleStyle()}><MapPin size={16}/></span>  {asset.location?.name ? Utility.shorten(asset.location.name, 12) : (<span style={{ fontWeight: 'bold', fontSize: '10px', paddingTop: '.25rem' }} className="text-danger">{t('location unknown')}</span>)}
                                </ListGroup.Item>
                                <ListGroup.Item className="d-flex align-items-center text-capitalize border-0">
                                    <span className="p-1 me-2" style={circleStyle(inspectionHasExpired ? 'danger' : 'primary')}><ClipboardX size={16}/></span>
                                    {Utility.formatAsDate(asset.last_inspection?.executed_at)?.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' })}
                                    {asset.last_inspection?.executed_at ? '' : (<span style={{ fontWeight: 'bold', fontSize: '10px', paddingTop: '.25rem' }} className="text-danger">{t('never inspected')}</span>)}
                                </ListGroup.Item>
                                <ListGroup.Item className="d-flex align-items-center text-capitalize border-0">
                                    <span className="p-1 me-2" style={circleStyle(inspectionHasExpired ? 'danger' : 'primary')}><ClipboardPen size={16} /></span>
                                    {Utility.formatAsDate(asset.last_inspection?.cover_to)?.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' })}
                                    {asset.last_inspection?.cover_to ? '' : (<span style={{ fontWeight: 'bold', fontSize: '10px', paddingTop: '.25rem' }} className="text-danger">{t('never inspected')}</span>)}
                                </ListGroup.Item>
                                <ListGroup.Item className="d-flex align-items-center text-capitalize border-0">
                                    <span className="p-1 me-2" style={circleStyle(asset.status?.name ? 'primary' : 'danger')}><ThumbsUp size={16} /></span>
                                    <span className="text-primary-emphasis">
                                        {asset.status?.name ? asset.status?.name : (<span style={{ fontWeight: 'bold', fontSize: '10px', paddingTop: '.25rem' }} className="text-danger">{t('status unknown')}</span>)}
                                    </span>
                                </ListGroup.Item>
                            </ListGroup>
                        </Card.Body>
                    </Col>

                    {/* Image Section */}
                    <Col xs={4}>
                        <div className="position-relative">
                            <div>
                                <Image
                                    src={asset.asset_image || assetImg}
                                    style={{ maxWidth: '100%', objectFit: 'cover', padding: '3px' }}
                                />
                            </div>
                            <div className="position-absolute" style={{right: 0, display: "flex", flexDirection: "column"}}>
                                { !!asset.manual &&
                                    <FancyButton id="manual-btn" icon={File} className="w-100"
                                                 clickFunction={() => window.open(asset.manual)} label={t('manual')}/>
                                }
                                { !!asset.video &&
                                    <FancyButton id="video-btn" icon={Play} className="w-100"
                                                 clickFunction={() => window.open(asset.video)} label={t('video')}/>
                                }
                            </div>
                        </div>
                    </Col>
                </Row>

                {!!showControls && (<Card.Body style={{padding: 0}}>
                    <div className={'fancy-button-container'}>
                        {!!cleanActivityType &&
                            <FancyButton id="clean-btn" iconUrl={cleanActivityType.icon} icon={Sparkles}
                                         clickFunction={() => handleAction('clean')} label={t('clean')}/>
                        }
                        {!!repairActivityType &&
                            <FancyButton id="repair-btn" iconUrl={repairActivityType.icon} icon={Wrench}
                                         clickFunction={() => handleAction('repair')} label={t('repair')}/>
                        }
                        {!!moveActivityType &&
                            <FancyButton id="pickup-btn" iconUrl={moveActivityType.icon} icon={ArrowRightLeft}
                                         clickFunction={() => handleAction('pickup')} label={t('pick up')}/>
                        }
                        <FancyButton id="other-btn" icon={ArrowRightLeft}
                                     clickFunction={() => handleAction('other')} label={t('Other')}/>
                    </div>

                </Card.Body>)}
            </Card>

            {!!showControls && (<BottomSheet isOpen={isSheetOpen} onClose={closeSheet}>
                <div className="mb-2">
                    <span style={{
                        marginBottom: '10px', marginRight: '10px',
                        fontSize: '18px', fontWeight: 'bolder',
                        padding: '5px', background: '#7aaafe'
                    }}>{t('inquiry')}</span>
                    <span style={{fontStyle: 'italic', fontSize: '14px'}}>{t('request ' + action)}</span>
                </div>

                <Formik
                    initialValues={initialValues}
                    key={action}
                    validationSchema={getValidationSchema(action)}
                    onSubmit={handleSubmit}
                    context={{ action }}
                    // onReset={() => setIsSheetOpen(false)}
                >
                    {({
                          errors, touched, values,
                          handleBlur, handleChange,
                          setFieldValue, resetForm, dirty
                      }) => {
                        // Try to reset: if dirty, ask; else just reset immediately
                        const tryReset = (e?: React.MouseEvent) => {
                            e?.preventDefault();
                            if (dirty || Object.keys(touched).length > 0) {
                                setPendingReset(() => () => {
                                    resetForm();
                                    setConfirmOpen(false);
                                    setIsSheetOpen(false);
                                    setCancelQR(true);
                                });
                                setConfirmOpen(true);
                            } else {
                                resetForm();
                                setIsSheetOpen(false);
                                setCancelQR(true);
                            }
                        };
                        return (
                            <IKForm style={{overflow: "auto", height: "100%"}}>

                                {action === 'other' ? (
                                    <>
                                        <Form.Label
                                        style={{paddingTop: "10px", paddingBottom: "0", marginBottom: 0}}>{t("activity.type")}</Form.Label>
                                        <Form.Select
                                            name="activity_type_id"
                                            value={values.activity_type_id ?? ''}
                                            onChange={(e) =>
                                                setFieldValue(
                                                    'activity_type_id',
                                                    e.target.value ? Number(e.target.value) : undefined
                                                )
                                            }                                            isValid={touched.activity_type_id && !errors.activity_type_id}
                                            onBlur={handleBlur}                           // <-- mark as touched
                                            isInvalid={Boolean(touched.activity_type_id && errors.activity_type_id)}
                                        >
                                            <option>{t('select_activity_type')}</option>
                                            {activityTypes.filter((type) => !['repair', 'move', 'clean'].includes(type.name.toLocaleLowerCase())).map((type) => (
                                                <option key={type.id} value={type.id}>{upperFirst(type.name)}</option>
                                            ))}
                                        </Form.Select>
                                        <Form.Control.Feedback type="invalid">
                                            {errors.activity_type_id}
                                        </Form.Control.Feedback>
                                    </>)
                                    : <></>}

                                <UIButton id='picture-taker' Icon={Camera} clickFunction={() => {
                                    setShow(true);
                                }} style={{width: '100%', marginBottom: '5px', marginTop: '10px'}}
                                          label={t('add_picture_to_request')}></UIButton>

                                <CameraRBModal
                                    show={show}
                                    name="file"
                                    onHide={() => {
                                        setShow(false);
                                    }}
                                    onPreview={(url) => setPreviewUrl(url)}
                                    toFilename="photo.jpg"         // ensures it's a File
                                    fullscreen // or: "md-down"
                                    title="Take a photo"
                                    onCapture={(blob) => {
                                        // handle the captured Blob
                                        const fd = new FormData();
                                        fd.append("photo", blob, "photo.jpg");
                                        // fetch("/upload", { method: "POST", body: fd });
                                        setShow(false);
                                    }}
                                />
                                {previewUrl && (
                                    <div className="d-flex align-items-center gap-2 mb-3" style={{
                                        margin: '15px',
                                        position: 'relative',
                                        justifyContent: 'center'
                                    }}>
                                        <img
                                            src={previewUrl}
                                            alt="preview"
                                            style={{width: 120, height: 120, objectFit: "cover", borderRadius: 8}}
                                        />
                                        <UIButton
                                            id="rm-img" variant="outline-danger"
                                            clickFunction={() => removeImage(setFieldValue)}
                                            Icon={Trash2}
                                            style={{
                                                position: 'absolute',
                                                top: '-10px',
                                                left: '75px',
                                                padding: '2px',
                                                background: 'white'
                                            }}
                                        ></UIButton>
                                    </div>
                                )}

                                <ScannerField scanOnload={false} label={t('location')} name='location_id'
                                              entity={asset.location} iconShakeInterval={iconShakeInterval}/>

                                <Form.Label
                                    style={{paddingTop: "10px", paddingBottom: "4px", marginBottom: 0}}>{t('priority')}</Form.Label>
                                <Form.Select
                                    name="priority"
                                    value={values.priority ?? ''}               // keep controlled
                                    onChange={(e) => setFieldValue('priority', e.target.value)}
                                    isValid={touched.priority && !errors.priority}
                                    onBlur={handleBlur}                           // <-- mark as touched
                                    isInvalid={Boolean(touched.priority && errors.priority)}
                                >
                                    <option value="">{t('select priority')}</option>
                                    <option value="1">1 - {t('low')}</option>
                                    <option value="2">2 - {t('medium')}</option>
                                    <option value="3">3 - {t('high')}</option>
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    {errors.priority}
                                </Form.Control.Feedback>

                                <Form.Label style={{
                                    paddingTop: "10px",
                                    paddingBottom: "4px", marginBottom: 0
                                }}>{t('Contact Person')}</Form.Label>
                                <Form.Control
                                    as="input"
                                    type="text"
                                    name="contact_person"
                                    value={values.contact_person}
                                    onChange={handleChange}
                                    isValid={touched.contact_person && !errors.contact_person}
                                />

                                <Form.Label
                                    style={{paddingTop: "10px", paddingBottom: "4px", marginBottom: 0}}>{t('remark')}</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    type="text"
                                    name="remark"
                                    value={values.remark}
                                    onChange={handleChange}
                                    isValid={touched.remark && !errors.remark}
                                />
                                {/*<UIButton variant='primary' type='submit' label={t('submit')} id='request-sub-btn' className="mt-3" />*/}
                                <div className={'fancy-button-container'} style={{marginLeft: 0, paddingLeft: 0}}>
                                    <FancyButton icon={ArrowBigLeft} reverse={true} type='button'
                                                 clickFunction={tryReset} label={t('back')} id='request-res-btn'
                                                 className="mt-3"/>
                                    <FancyButton icon={ArrowBigRight} type='submit' label={t('submit')}
                                                 id='request-sub-btn-1' className="mt-3"/>
                                </div>
                                <Modal show={confirmOpen} onHide={() => setConfirmOpen(false)} centered>
                                    <Modal.Header closeButton>
                                        <Modal.Title>{t("Discard changes?")}</Modal.Title>
                                    </Modal.Header>
                                    <Modal.Body>
                                        {t("You have unsaved changes. Do you want to reset the form?")}
                                    </Modal.Body>
                                    <Modal.Footer>
                                        <Button variant="secondary" onClick={() => setConfirmOpen(false)}>
                                            {t("Cancel")}
                                        </Button>
                                        <Button
                                            variant="danger"
                                            onClick={() => pendingReset?.()}
                                        >
                                            {t("Reset")}
                                        </Button>
                                    </Modal.Footer>
                                </Modal>
                            </IKForm>
                        )
                    }}
                </Formik>
            </BottomSheet>)}
        </div>
    );
}

const mapStateToProps = (state: any) => ({
    activityTypes: state.asset.activityTypes
});

export default connect(mapStateToProps)(Asset);
